import { Range, TimelineRange, CursorMillis, TimelineCoordinate } from './timeline-common';
import { Observable, merge } from 'rxjs';
import { debounceTime, map as mapEvent } from 'rxjs/operators';
import { TimelineOverlayCategory } from './timeline-overlay';

export const createAnimationScriptObservable = ( timeline: Timeline ) : Observable<CursorMillis[]> => {
  return  timeline.getObservable('mapp-tl-an-scrpt')
      .pipe(
        mapEvent((event: any[]) => {
          return <CursorMillis[]>event[1];
        })
      );
}

export const createHeightObservable = ( timeline: Timeline ) : Observable<{ height: number }> => {
    return timeline.getObservable('mapp-tl-hght')
      .pipe(
        mapEvent((event: any[]) => {
          return { height: <number>event[1] };
        })
      );
}
  
export const createTimeCursorObservable = ( timeline: Timeline ) : Observable<CursorMillis> => {
  let cursorRemovedObservable: Observable<CursorMillis> =
    timeline.getObservable('mapp-tl-cur-del')
      .pipe(
        mapEvent((event: any) => {
          return undefined;
        })
      );
  let timeCursorObservable: Observable<CursorMillis> =
    timeline.getObservable('mapp-tl-cur-pos')
      .pipe(
        mapEvent((event: any[]) => {
          return <CursorMillis>event[1];
        })
      );
   return merge(
    cursorRemovedObservable,
    timeCursorObservable
  )
}

export const createAnimationObservable = ( timeline: Timeline ) : Observable<{ animating: boolean }> => {
  return merge(
    timeline.getObservable('mapp-tl-an-act').pipe(mapEvent(evt => { return { animating: true }; })),
    timeline.getObservable('mapp-tl-an-dact').pipe(mapEvent(evt => { return { animating: false }; }))
  )
}

export const createControlsObservable = ( timeline: Timeline ) : Observable<any> => {
  return timeline.getObservable('tlmd-tl-ctrl-update');
}

export const createTimeRangeObservable = (timeline: Timeline, bounceTime?: number): Observable<TimelineRange> => {
  return timeline.getObservable('mapp-tl-rng')
      .pipe(
        debounceTime(bounceTime !== undefined && bounceTime >= 0 ? bounceTime : 250),
        mapEvent((event: any[]) => {
          return <TimelineRange>event[1];
        })
      );
}

export enum OverlayType {
  Standard = 1, //overlays that have no special dragging logic
  Static = 2,   //overlays that do not move and handle all positioning logic themselves
  Animated = 4 //overlays who change their appearance based on the position of the time cursor and therefore will be notified of the time cursor changes
}
export interface Projection {
  constructor(timeline: Timeline): void,
  fromPointToMillis(timePoint: number): number,
  fromMillisToPoint(millis: number, visible?: boolean, start?: boolean): number,
  fromMillisRangeToPointRange(millis: Range, visible?: boolean): Range,
  getMspp(): number
}

export enum TimelineEvents {//there are more but only these ones are used within TS code base 
  UPDATE_CONTROLS = 'tlmd-tl-ctrl-update'
}
export enum TimelineModes {
  STATIC = 'static',
  SYNCHRONIZED = 'synchronized',
  STATIC_ANIMATION = 'static-animation',
  SYNCHRONIZED_ANIMATION = 'synchronized-animation',
  STATIC_INSPECTION = 'static-inspection',
  SYNCHRONIZED_INSPECTION = 'synchronized-inspection'
}

export interface Timeline {
  /**
   * @return the unique is among the instances of this class
   */
  getId(): string,
  /**
   * return the internal top level div that contains all timeline dom elements. N.B. This 
   * is not the element referenced by the containerNodeOrSelector.
   *  */
  getContainer(): HTMLDivElement,
  /**
   * returns an array of HTMLElements that represent a data  layer on the timeline
   */
  getLayers(): HTMLElement[],
  /**
   * returns a map that has the layerIds as the key and the actual DOM element that represents the layer on the timeline
   */
  getLayerMap(): { [key: string]: HTMLElement },
  /**
   * Adds a new layer at the top of the timeline. In order for the overlay to receive
   * notifications from the timeline, the registerOverlay() method needs to be called.
   * @param layerId the id of the layer
   * @param height the height of the layer upon add
   * @param isStatic whether this layer moves upon dragging horizontally
   * @return the added layer DOM element 
   */
  addLayer(layerId: string, index:number, category: TimelineOverlayCategory, height: number, isStatic?: boolean): HTMLElement,
  /**
   * remove the layer with given id from the timeline
   * @param layerId the id of the layer to remove
   * @return the removed layer DOM element
   */
  removeLayer(layerId: string): HTMLElement,
  /**
   * return the layer with given id from the timeline
   * @param layerId the id of the layer to return
   * @return the returned layer DOM element
   */
  getLayer(layerId: string): HTMLElement,

  /**
   * set the height of the layer with given id to the given height
   * @param layerId the id of the layer to set the height for
   * @param height the height to set
   */
  setLayerHeight(layerId: string, height: number): HTMLElement,

  /**
   * An internal API method where the com.tlmapp.timeline.OverlayView registers
   * itself to the timeline. Registered overlays get called to draw() when the
   * timeline changed
   * 
   * @param overlayId the overlayId that references to the overlay
   * @param overlay The overlay to register
   */
  registerOverlay(overlayId: string, overlay: any): void,
  /**
   * An internal API method where the com.tlmapp.timeline.OverlayView unregisters
   * itself from the timeline.
   * 
   * @param overlayId the overlayId that references to the overlay to be unregistered.
   * @return the unregistered overlay
   */
  unregisterOverlay(overlayId: string): any,
  getProjection(): Projection,
  getTimeRange(includeCursorMillis?: boolean): TimelineRange,
  getWidth(): number,
  getRangePixelWidth(): number,
  setRange(visibleStartMillis: number, visibleEndMillis: number): void,
  zoom(step: number, timeCoordinate?: TimelineCoordinate): void,
  reset(): void,
  synchronize(synchronize: boolean): void,
  showCursor( show: boolean ): void,
  startAnimation(): void,
  pauseAnimation(pause: boolean): void,
  subsscribeToTimeCursor(onCursorMove: (event: Event, cursorMillis: CursorMillis) => void, onCursorRemove?: (event: Event) => void): void,
  unsubsscribeFromTimeCursor(onCursorMove: (event: Event, cursorMillis: CursorMillis) => void, onCursorRemove?: (event: Event) => void): void,
  getAnimationTiming(): CursorMillis[],
  getGranularity(): string,
  getCursorMillis(onlyWhenVisible?: boolean): CursorMillis,
  getMode(): string,

  //query methods (used by the timeline controls)
  isCursorEnabled(): boolean,
  isCursorVisible(): boolean,
  isCursorAnimationEnabled(): boolean,
  isCursorAnimationActive(): boolean,
  inSimpleMode(): boolean,
  isCursorAnimationPaused(): boolean,
  isAnimating(): boolean;
  isTimeSynchronisationEnabled(): boolean,
  isSynchronizing(): boolean,
  isLagging(): boolean

  //get observables from the timeline
  getObservable(event: string): Observable<any>

}
